import { useEffect, useState } from "react";
import Link from "next/link";
import { useAtomValue } from "jotai";
import { Box, Chip, Skeleton, Tooltip, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { negativeState, ortexColorState, themeState } from "@/lib/store";
import { nFormatter } from "@/lib/utils";
import { getLockUpSummary, LockUpHighlight, LockUpSummary } from "@/controllers/lock-ups";
import LockUpWeeks from "./LockUpWeeks";

/**
 * The band above the lock-ups grid.
 *
 * The page used to open on a grid of dates and a column of CIQ prose, sorted by
 * whichever expiry happened to be soonest. That answers "what is next" and
 * nothing else — and "next" is rarely the question, because the next expiry is
 * usually a thirty-day executive lock-up at a mega-cap that moves nothing. The
 * questions people actually arrive with are how much supply is coming and which
 * of it matters, so the band answers those first: the totals per horizon, the
 * shape of the next quarter, and the handful big enough to be worth naming.
 *
 * One request for all of it. Three panels each fetching their own slice would be
 * three round trips on a block whose whole job is to be read before the grid
 * below it has finished loading.
 */

/** Compact a dollar figure for a stat tile. */
export const formatUsd = (value: number | null | undefined): string => (value ? `$${nFormatter(value, 1)}` : "—");

/**
 * How a block's size relative to free float should read.
 *
 * Past 100% the block is bigger than the entire tradeable register — usually a
 * promoter or pre-IPO holding that was never in the float — and "1,847%" is a
 * number nobody parses. Multiples are how people say this out loud.
 */
export const formatFloatShare = (pct: number | null | undefined): string => {
    if (pct === null || pct === undefined) return "—";
    if (pct >= 200) return `${(pct / 100).toFixed(1)}× float`;
    return `${pct.toFixed(pct < 10 ? 1 : 0)}% of float`;
};

/** Where a highlight card points. Mirrors the grid's link target. */
export const stockHref = (highlight: LockUpHighlight): string | null => {
    if (highlight.exchange && highlight.ticker) return `/s/${highlight.exchange}/${highlight.ticker}/corporate-actions/all`;
    if (highlight.stockId) return `/id/${highlight.stockId}/corporate-actions`;
    return null;
};

/** "in 4 days" / "tomorrow" / "today" — a countdown reads faster than a date. */
export const formatCountdown = (days: number | null | undefined): string => {
    if (days === null || days === undefined) return "";
    if (days <= 0) return "today";
    if (days === 1) return "tomorrow";
    if (days < 7) return `in ${days} days`;
    if (days < 60) return `in ${Math.round(days / 7)} weeks`;
    return `in ${Math.round(days / 30)} months`;
};

/**
 * What the band renders before its request lands: the same shape as a typical
 * answer, so the placeholder occupies the loaded band's height at every
 * breakpoint and the grid below does not jump when the figures arrive. Its text
 * is never shown: the values become skeleton bars and the prose is hidden.
 */
const placeholderHighlight = (index: number): LockUpHighlight => ({
    name: "Placeholder Company Holdings Ltd.",
    ticker: "TICKER",
    exchange: "",
    stockId: index,
    securityId: null,
    expiryDate: null,
    daysUntil: 30,
    sharesLocked: 100000000,
    pctOfFreeFloat: 50,
    daysOfVolume: 100,
    valueUsd: 1000000000,
    siPctFreeFloat: null,
    securityClass: "Ordinary Shares",
});

const PLACEHOLDER: LockUpSummary = {
    asOfDate: "",
    horizonDays: 180,
    totalUpcoming: 1000,
    totalCompanies: 0,
    countries: 40,
    nextExpiryDate: null,
    // Typical magnitudes, so the hidden sub-lines take their usual width and the
    // tiles wrap the way the loaded ones do.
    windows: {
        week: { days: 7, expiries: 50, companies: 50, valueUsd: 10.5e9, shares: null },
        month: { days: 30, expiries: 300, companies: 200, valueUsd: null, shares: null },
        quarter: { days: 90, expiries: 800, companies: 500, valueUsd: 200.5e9, shares: null },
    },
    calendar: Array.from({ length: 12 }, (_, index) => ({ weekStart: new Date(Date.UTC(2026, 0, 5 + 7 * index)).toISOString().slice(0, 10), count: 1, valueUsd: null, shares: null })),
    highlights: [0, 1, 2, 3].map(placeholderHighlight),
    coverage: { sized: 600, significant: 0, linked: 1000 },
};

/** Hides placeholder prose while keeping the space it takes. */
const ghostSx = (ghost?: boolean) => (ghost ? { visibility: "hidden" as const } : {});

const StatTile = ({ label, value, sub, ghost }: { label: string; value: string; sub?: string; ghost?: boolean }) => (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 104 }}>
        <Typography sx={{ fontSize: "1.6rem", fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: "proportional-nums" }}>{ghost ? <Skeleton width={64} /> : value}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {label}
        </Typography>
        {sub && (
            <Typography variant="caption" sx={{ color: "text.secondary", ...ghostSx(ghost) }}>
                {sub}
            </Typography>
        )}
    </Box>
);

const HighlightCard = ({ highlight, ghost }: { highlight: LockUpHighlight; ghost?: boolean }) => {
    const theme = useAtomValue(themeState);
    const ortexColor = useAtomValue(ortexColorState);
    const negative = useAtomValue(negativeState);
    const isDark = theme === "dark";
    const border = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)";
    const surface = isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.9)";
    const href = ghost ? null : stockHref(highlight);

    const body = (
        <>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, ...ghostSx(ghost) }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>{highlight.ticker || highlight.name}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {highlight.name}
                    </Typography>
                </Box>
                {href && <ArrowForwardIcon className="card-go" sx={{ fontSize: 16, opacity: 0, color: ortexColor, flexShrink: 0 }} />}
            </Box>

            {/* The one number the card exists to show. Ink, not the accent colour —
                the emphasis is size and position, and colouring a figure by its
                magnitude would imply a good/bad reading that an unlock does not have. */}
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 600, lineHeight: 1.15, marginTop: "6px" }}>{ghost ? <Skeleton width="55%" /> : formatFloatShare(highlight.pctOfFreeFloat)}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", ...ghostSx(ghost) }}>
                {[highlight.sharesLocked ? `${nFormatter(highlight.sharesLocked, 1)} shares` : null, highlight.valueUsd ? formatUsd(highlight.valueUsd) : null, highlight.daysOfVolume ? `${nFormatter(highlight.daysOfVolume, 1)}d volume` : null]
                    .filter(Boolean)
                    .join(" · ")}
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, marginTop: "auto", paddingTop: "8px", ...ghostSx(ghost) }}>
                <Chip size="small" variant="outlined" label={formatCountdown(highlight.daysUntil)} sx={{ height: 19, fontSize: "0.65rem" }} />
                {highlight.securityClass && <Chip size="small" variant="outlined" label={highlight.securityClass} sx={{ height: 19, fontSize: "0.65rem" }} />}
                {/* Short interest is not part of the ranking, but an unlock landing into
                    a crowded short is a different setup from one landing into an empty
                    book — so it is shown where it exists and omitted where it does not. */}
                {highlight.siPctFreeFloat !== null && highlight.siPctFreeFloat >= 10 && (
                    <Tooltip title="Short interest as a percentage of free float">
                        <Chip size="small" variant="outlined" label={`${highlight.siPctFreeFloat.toFixed(0)}% SI`} sx={{ height: 19, fontSize: "0.65rem", borderColor: negative, color: negative }} />
                    </Tooltip>
                )}
            </Box>
        </>
    );

    const sx = {
        display: "flex",
        flexDirection: "column",
        padding: "12px 14px",
        borderRadius: "10px",
        border: `1px solid ${border}`,
        backgroundColor: surface,
        textDecoration: "none",
        color: "inherit",
        height: "100%",
        transition: "border-color 0.15s ease, transform 0.15s ease",
        "&:hover": { borderColor: ortexColor, transform: "translateY(-1px)" },
        "&:hover .card-go": { opacity: 1 },
    } as const;

    // A company ORTEX does not cover renders as a card without a link rather than
    // as a link to nowhere — the same rule the grid's company column follows.
    if (!href) return <Box sx={{ ...sx, "&:hover": { borderColor: border } }}>{body}</Box>;

    return (
        <Link passHref legacyBehavior href={href}>
            <Box component="a" sx={sx}>
                {body}
            </Box>
        </Link>
    );
};

const LockUpsSummary = () => {
    const theme = useAtomValue(themeState);
    const [payload, setPayload] = useState<LockUpSummary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const controller = new AbortController();
        getLockUpSummary(controller.signal)
            .then(setPayload)
            .catch(() => setPayload(null))
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    // While loading, the band renders in full from PLACEHOLDER so it already has
    // its loaded height and the grid below does not move when the data lands.
    const ghost = loading && !payload;

    // The grid below stands on its own, so a failed summary hides the band rather
    // than replacing the page with an error.
    if (!ghost && !payload) return null;

    const data = payload ?? PLACEHOLDER;
    const isDark = theme === "dark";
    const week = data.windows?.week;
    const month = data.windows?.month;
    const quarter = data.windows?.quarter;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", padding: "4px 2px 12px" }} aria-busy={ghost || undefined}>
            <Box
                sx={{
                    padding: { xs: "14px", md: "18px 22px" },
                    borderRadius: "12px",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                    background: isDark ? "linear-gradient(120deg, rgba(49,171,166,0.14) 0%, rgba(255,255,255,0.02) 55%)" : "linear-gradient(120deg, rgba(49,171,166,0.12) 0%, rgba(0,0,0,0.015) 55%)",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(300px, 460px)" },
                    gap: { xs: "18px", md: "28px" },
                    alignItems: "start",
                }}
            >
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", marginBottom: "2px" }}>Lock-up expiries</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 620, marginBottom: "14px" }}>
                        When restricted shares become sellable, sized against each company&apos;s free float, trading volume and last close, so you can tell an overhang from a formality.
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2.5, md: 4 } }}>
                        <StatTile ghost={ghost} label="This week" value={String(week?.expiries ?? 0)} sub={`${formatUsd(week?.valueUsd)} unlocking`} />
                        <StatTile ghost={ghost} label="Next 30 days" value={String(month?.expiries ?? 0)} sub={`${month?.companies ?? 0} companies`} />
                        <StatTile ghost={ghost} label="Next 90 days" value={String(quarter?.expiries ?? 0)} sub={`${formatUsd(quarter?.valueUsd)} unlocking`} />
                        <StatTile ghost={ghost} label={`Next ${data.horizonDays} days`} value={nFormatter(data.totalUpcoming, 0)} sub={`${data.countries} countries`} />
                    </Box>
                </Box>

                <LockUpWeeks weeks={data.calendar} ghost={ghost} />
            </Box>

            {!!data.highlights?.length && (
                <Box>
                    <Typography variant="subtitle2" component="h2" sx={{ marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary" }}>
                        Biggest expiries ahead
                    </Typography>
                    {/* Deterministic column counts rather than `auto-fit`: four cards
                        against a `minmax(220px, 1fr)` track wrapped to three-plus-one at
                        some widths, and an orphaned card reads as a layout fault. */}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: "12px" }}>
                        {data.highlights.map((highlight) => (
                            <HighlightCard ghost={ghost} key={`${highlight.stockId ?? highlight.name}-${highlight.expiryDate}-${highlight.securityClass}`} highlight={highlight} />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Stated, not buried: CIQ names a share count on only about half its
                lock-up headlines, so the totals above are built from the half that
                can be sized. A reader adding up the grid and getting a different
                number needs to know that here, not in a support ticket. */}
            <Typography variant="caption" sx={{ color: "text.secondary", ...ghostSx(ghost) }}>
                {data.coverage.sized} of {data.totalUpcoming} upcoming expiries state a share count and can be sized against float; the rest are shown with their dates and terms. {data.coverage.linked} are matched to a stock you can open.
            </Typography>
        </Box>
    );
};

export default LockUpsSummary;
